import type React from "react";
import { useEffect, useState } from "react";
import { Alert, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import type translations from "../translations/translations.json";
import type { AppDispatch, RootState } from "../store";
import { fetchUsers, setUser } from "../store/features/userSlice";
import FormLayout from "../components/common/FormLayout";
import ProfilePictureSection from "../components/profile/ProfilePictureSection";
import ProfileNameSection from "../components/profile/ProfileNameSection";
import ProfileTimezoneSection from "../components/profile/ProfileTimezoneSection";
import ProfileDensitySection from "../components/profile/ProfileDensitySection";
import ProfilePasswordSection from "../components/profile/ProfilePasswordSection";
import { getUserProfileApi } from "../api/usersApi";
import { canEditProfile, canViewProfile, type User } from "../types/user";

type TranslationKeys = keyof typeof translations.en;

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { userId: routeUserId } = useParams<{ userId?: string }>();
  const currentUser = useSelector((state: RootState) => state.user.user);
  const [targetUser, setTargetUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSelf = !routeUserId || routeUserId === currentUser?._id;

  useEffect(() => {
    if (!currentUser) {
      setTargetUser(null);
      return;
    }

    if (isSelf) {
      setTargetUser(currentUser);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getUserProfileApi(routeUserId)
      .then((user) => {
        if (!cancelled) {
          setTargetUser(user);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTargetUser(null);
          setError(t("profile.accessDenied" as TranslationKeys));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser, isSelf, routeUserId, t]);

  const handleUserUpdated = (updated: User) => {
    setTargetUser(updated);
    if (isSelf) {
      dispatch(setUser(updated));
    } else {
      dispatch(fetchUsers());
    }
  };

  if (!currentUser) {
    return (
      <FormLayout title={t("Profile" as TranslationKeys)} maxWidth={720}>
        <Typography color="error">{t("User not found" as TranslationKeys)}</Typography>
      </FormLayout>
    );
  }

  if (loading) {
    return (
      <FormLayout title={t("Profile" as TranslationKeys)} maxWidth={720}>
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Stack>
      </FormLayout>
    );
  }

  if (!targetUser || error) {
    return (
      <FormLayout title={t("Profile" as TranslationKeys)} maxWidth={720}>
        <Alert severity="error">{error || t("User not found" as TranslationKeys)}</Alert>
      </FormLayout>
    );
  }

  if (!canViewProfile(currentUser, targetUser)) {
    return (
      <FormLayout title={t("Profile" as TranslationKeys)} maxWidth={720}>
        <Alert severity="error">{t("profile.accessDenied" as TranslationKeys)}</Alert>
      </FormLayout>
    );
  }

  const canEdit = canEditProfile(currentUser, targetUser);
  const sectionProps = {
    user: targetUser,
    canEdit,
    isSelf,
    onUserUpdated: handleUserUpdated,
  };

  const title = isSelf
    ? t("Profile" as TranslationKeys)
    : `${t("Profile" as TranslationKeys)} — ${targetUser.name || targetUser._id}`;

  return (
    <FormLayout title={title} maxWidth={720}>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t("Username" as TranslationKeys)}: {targetUser._id}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Role" as TranslationKeys)}: {targetUser.role || "—"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("profile.companyId" as TranslationKeys)}: {targetUser.companyId}
        </Typography>
      </Stack>

      <Stack spacing={4} divider={<Divider />}>
        <ProfilePictureSection {...sectionProps} />
        <ProfileNameSection {...sectionProps} />
        <ProfileTimezoneSection {...sectionProps} />
        {isSelf ? <ProfileDensitySection {...sectionProps} /> : null}
        {isSelf ? <ProfilePasswordSection /> : null}
      </Stack>
    </FormLayout>
  );
};

export default ProfilePage;
